import { useEffect, useRef, useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';

import { Input } from '@/src/components/Input';
import { colors } from '@/src/theme/colors';
import { spacing } from '@/src/theme/spacing';

import { checkUsernameAvailability } from './profile-service';
import { normalizeUsernameInput, usernameValidationMessage } from './username';

const USERNAME_AVAILABILITY_CHECK_DELAY_MS = 450;

export type UsernameAvailabilityStatus =
  | 'empty'
  | 'invalid'
  | 'checking'
  | 'available'
  | 'unavailable'
  | 'error'
  | 'claimed';

export type UsernameAvailability = {
  available: boolean;
  message: string | null;
  status: UsernameAvailabilityStatus;
  username: string;
};

export const emptyUsernameAvailability: UsernameAvailability = {
  available: false,
  message: null,
  status: 'empty',
  username: '',
};

export function UsernameAvailabilityField({
  disabled = false,
  error,
  onAvailabilityChange,
  onBlur,
  onChange,
  value,
}: {
  disabled?: boolean;
  error?: string;
  onAvailabilityChange: (availability: UsernameAvailability) => void;
  onBlur?: () => void;
  onChange: (value: string) => void;
  value: string;
}) {
  const [availability, setAvailability] = useState<UsernameAvailability>(
    emptyUsernameAvailability,
  );
  const requestIdRef = useRef(0);
  const username = normalizeUsernameInput(value ?? '');
  const statusMessage = error ?? availability.message;
  const showStatus =
    Boolean(statusMessage) ||
    availability.status === 'checking' ||
    availability.status === 'available';

  useEffect(() => {
    onAvailabilityChange(availability);
  }, [availability, onAvailabilityChange]);

  useEffect(() => {
    const normalizedUsername = normalizeUsernameInput(value ?? '');

    requestIdRef.current += 1;

    if (disabled) {
      setAvailability({
        available: true,
        message: 'Username already claimed.',
        status: 'claimed',
        username: normalizedUsername,
      });
      return;
    }

    if (!normalizedUsername) {
      setAvailability(emptyUsernameAvailability);
      return;
    }

    const validationMessage = usernameValidationMessage(normalizedUsername);

    if (validationMessage) {
      setAvailability({
        available: false,
        message: validationMessage,
        status: 'invalid',
        username: normalizedUsername,
      });
      return;
    }

    const requestId = requestIdRef.current;
    setAvailability({
      available: false,
      message: 'Checking availability...',
      status: 'checking',
      username: normalizedUsername,
    });

    const timeout = setTimeout(() => {
      void checkUsernameAvailability(normalizedUsername)
        .then((result) => {
          if (requestIdRef.current !== requestId) {
            return;
          }

          setAvailability({
            available: result.available,
            message: result.available
              ? 'That username is available.'
              : (result.reason ?? 'That username is not available.'),
            status: result.available ? 'available' : 'unavailable',
            username: result.normalizedUsername,
          });
        })
        .catch(() => {
          if (requestIdRef.current !== requestId) {
            return;
          }

          setAvailability({
            available: false,
            message: 'Unable to check availability. Try again in a moment.',
            status: 'error',
            username: normalizedUsername,
          });
        });
    }, USERNAME_AVAILABILITY_CHECK_DELAY_MS);

    return () => {
      clearTimeout(timeout);
    };
  }, [disabled, value]);

  return (
    <View style={styles.field}>
      <Input
        label="Username"
        autoCapitalize="none"
        autoCorrect={false}
        editable={!disabled}
        onBlur={onBlur}
        onChangeText={(text) => {
          onChange(normalizeUsernameInput(text));
        }}
        placeholder="jane_doe"
        textContentType="username"
        value={value}
        error={error}
      />
      <Text style={styles.help}>
        Your public profile link will be frienzzi.com/profile/
        {username || 'username'}.
      </Text>
      {showStatus ? (
        <Text
          style={[
            styles.status,
            !error && availability.status === 'available'
              ? styles.available
              : null,
            availability.status === 'invalid' ||
            availability.status === 'unavailable' ||
            availability.status === 'error' ||
            error
              ? styles.unavailable
              : null,
          ]}
        >
          {statusMessage}
        </Text>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  field: {
    gap: spacing.xs,
  },
  help: {
    color: colors.mutedText,
    fontSize: 13,
    lineHeight: 18,
  },
  status: {
    color: colors.mutedText,
    fontSize: 13,
    fontWeight: '700',
    lineHeight: 18,
  },
  available: {
    color: colors.success,
  },
  unavailable: {
    color: colors.danger,
  },
});
